import { Appointment, Case, CaseStatus, ChurchEvent } from '../../types';
import { getAppointmentDateTime, toDateKey } from '../Calendar/calendarUtils';
import { isMeetingFrequencyOverdue } from '../../utils/meetingFrequency';
import { t } from '../../utils/translations';

export interface ActivityRecord {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: Date;
  userId?: string;
  userName?: string;
  metadata?: Record<string, unknown>;
}

export interface AppointmentGroup {
  label: string;
  items: Appointment[];
}

const STATUS_LABELS: Record<CaseStatus, string> = {
  waiting: 'În așteptare',
  active: 'Activ',
  unfinished: 'Neterminat',
  finished: 'Finalizat',
  cancelled: 'Anulat',
};

export function getStatusLabel(status: CaseStatus): string {
  return STATUS_LABELS[status] ?? status;
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  waiting: 'bg-amber-50 text-amber-700',
  finished: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-slate-100 text-slate-600',
  unfinished: 'bg-red-50 text-red-700',
};

export function getStatusBadgeClass(status: CaseStatus): string {
  return STATUS_BADGE_CLASSES[status] ?? 'bg-slate-100 text-slate-600';
}

const AVATAR_COLORS = [
  'bg-brand-100 text-brand-600',
  'bg-amber-100 text-amber-700',
  'bg-green-100 text-green-700',
  'bg-sky-100 text-sky-700',
];

export function getAvatarColorClass(name: string): string {
  const index = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export function getCaseDisplayId(caseItem: Case): string {
  return `#C-${caseItem.id.slice(-4).toUpperCase()}`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function formatTimeAgo(timestamp: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - timestamp.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Chiar acum';
  if (diffMins < 60) return `${diffMins} min în urmă`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'oră' : 'ore'} în urmă`;
  if (diffDays === 1) return t.dashboard.yesterday;
  if (diffDays < 7) return `${diffDays} zile în urmă`;

  return timestamp.toLocaleDateString('ro-RO', {
    month: 'short',
    day: 'numeric',
    year: timestamp.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function formatDateLabel(timestamp: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());

  if (target.getTime() === today.getTime()) return t.dashboard.today;
  if (target.getTime() === tomorrow.getTime()) return 'Mâine';
  return timestamp.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'short' });
}

export type UpcomingScheduleItem =
  | {
      kind: 'appointment';
      id: string;
      date: Date;
      startTime: string;
      label: string;
      /** e.g. „Sesiunea #2” — shown right-aligned with space from the name. */
      sessionLabel?: string | null;
    }
  | { kind: 'event'; id: string; date: Date; startTime: string; label: string; isMultiDay: boolean };

export interface UpcomingScheduleGroup {
  label: string;
  items: UpcomingScheduleItem[];
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function buildUpcomingScheduleItems(
  appointments: Appointment[],
  events: { event: ChurchEvent; displayDate: Date }[],
  now = new Date()
): UpcomingScheduleItem[] {
  const appointmentItems: UpcomingScheduleItem[] = appointments
    .filter((apt) => {
      const appointmentDateTime = new Date(apt.date);
      if (apt.startTime) {
        const [hours, minutes] = apt.startTime.split(':').map(Number);
        appointmentDateTime.setHours(hours, minutes, 0, 0);
      }
      return appointmentDateTime > now;
    })
    .map((apt) => ({
      kind: 'appointment' as const,
      id: apt.id,
      date: apt.date,
      startTime: apt.startTime,
      label: getAppointmentDisplayName(apt),
      sessionLabel:
        typeof apt.sessionNumber === 'number'
          ? `${t.sessionReports.sessionNumber} #${Math.max(0, Math.floor(apt.sessionNumber))}`
          : null,
    }));

  const eventItems: UpcomingScheduleItem[] = events.map(({ event, displayDate }) => ({
    kind: 'event' as const,
    id: event.id,
    date: displayDate,
    startTime: event.startTime,
    label: event.name,
    isMultiDay: startOfDay(event.startDate).getTime() !== startOfDay(event.endDate).getTime(),
  }));

  return [...appointmentItems, ...eventItems].sort((a, b) => {
    const dateCompare = a.date.getTime() - b.date.getTime();
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });
}

export function groupUpcomingScheduleByDay(items: UpcomingScheduleItem[]): UpcomingScheduleGroup[] {
  const groups = new Map<string, UpcomingScheduleItem[]>();

  items.forEach((item) => {
    const label = formatDateLabel(item.date);
    const existing = groups.get(label) ?? [];
    existing.push(item);
    groups.set(label, existing);
  });

  return Array.from(groups.entries()).map(([label, groupItems]) => ({
    label,
    items: groupItems.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  }));
}

export function groupAppointmentsByDay(appointments: Appointment[]): AppointmentGroup[] {
  const groups = new Map<string, Appointment[]>();

  appointments.forEach((apt) => {
    const label = formatDateLabel(apt.date);
    const existing = groups.get(label) ?? [];
    existing.push(apt);
    groups.set(label, existing);
  });

  return Array.from(groups.entries()).map(([label, items]) => ({
    label,
    items: items.sort((a, b) => a.startTime.localeCompare(b.startTime)),
  }));
}

/** Progress heuristic: ~15% per session report, capped at 100%. */
export function getCaseProgress(status: CaseStatus, sessionCount: number): number {
  if (status === 'finished') return 100;
  if (status === 'cancelled') return 0;
  if (sessionCount > 0) return Math.min(100, sessionCount * 15);
  if (status === 'active') return 10;
  return 0;
}

export const SESSION_ROAD_MIN_NODES = 5;
export const SESSION_ROAD_EXPANDED_NODES = 8;
export const SESSION_ROAD_EXPAND_AT = 3;
/** Narrow cards fit fewer nodes; more would shrink the dots to an unreadable size. */
export const SESSION_ROAD_NARROW_NODES = 6;
/**
 * Midnight, `days` days ago, in local time.
 *
 * Day-aligned deliberately. A cutoff built straight from `Date.now()` differs by milliseconds
 * on every mount, so Firestore sees a different query each time and the persistent cache cannot
 * resume the previous one — the whole result set is refetched on every visit, which defeats the
 * point of bounding it. Aligned to midnight the query is identical all day, so the cache
 * returns what it already has and only the delta is fetched.
 */
export function startOfDayDaysAgo(days: number, now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
}

/** Planned sessions kept visible after the next one, so the road always looks ahead. */
export const SESSION_ROAD_FUTURE_NODES = 2;

export type SessionRoadNode = {
  session: number;
  state: 'done' | 'next' | 'open';
};

export function getSessionRoadCapacity(
  reportCount: number,
  maxNodes = SESSION_ROAD_EXPANDED_NODES
): number {
  const expanded =
    reportCount >= SESSION_ROAD_EXPAND_AT
      ? SESSION_ROAD_EXPANDED_NODES
      : SESSION_ROAD_MIN_NODES;
  return Math.min(expanded, Math.max(1 + SESSION_ROAD_FUTURE_NODES, maxNodes));
}

/**
 * 0–2 reports: 5 nodes. 3rd report: expand to 8 (compressed), or to maxNodes on
 * narrow cards. The window always ends with the next session plus
 * SESSION_ROAD_FUTURE_NODES planned ones, so finished sessions scroll out of
 * view rather than pushing the road ahead out of it. Older done sessions are
 * then reported as hiddenDone.
 */
export function buildSessionRoad(
  reportCount: number,
  maxNodes = SESSION_ROAD_EXPANDED_NODES
): {
  hiddenDone: number;
  nodes: SessionRoadNode[];
  nextSession: number;
  capacity: number;
} {
  const count = Math.max(0, Math.floor(reportCount));
  const capacity = getSessionRoadCapacity(count, maxNodes);
  const doneSlots = Math.max(0, capacity - 1 - SESSION_ROAD_FUTURE_NODES);
  const hiddenDone = Math.max(0, count - doneSlots);
  const nodes: SessionRoadNode[] = [];
  for (let session = hiddenDone; session < hiddenDone + capacity; session += 1) {
    if (session < count) nodes.push({ session, state: 'done' });
    else if (session === count) nodes.push({ session, state: 'next' });
    else nodes.push({ session, state: 'open' });
  }
  return { hiddenDone, nodes, nextSession: count, capacity };
}

export function getAppointmentDisplayName(appointment: Appointment): string {
  return appointment.caseTitle ?? appointment.title;
}

/** Earliest future appointment per case (by start time). Skips appointments without caseId. */
export function buildNextAppointmentByCaseId(
  appointments: Appointment[],
  now = new Date()
): Record<string, Appointment> {
  const map: Record<string, Appointment> = {};
  for (const apt of appointments) {
    if (!apt.caseId) continue;
    const start = getAppointmentDateTime(apt, apt.startTime);
    if (start <= now) continue;
    const existing = map[apt.caseId];
    if (!existing || start < getAppointmentDateTime(existing, existing.startTime)) {
      map[apt.caseId] = apt;
    }
  }
  return map;
}

/**
 * Earliest future appointment per case and session number. Only appointments
 * labelled with a session are indexed, so the road can tell which of its nodes
 * is already booked.
 */
export function buildSessionAppointmentsByCaseId(
  appointments: Appointment[],
  now = new Date()
): Record<string, Record<number, Appointment>> {
  const map: Record<string, Record<number, Appointment>> = {};
  for (const apt of appointments) {
    if (!apt.caseId) continue;
    if (typeof apt.sessionNumber !== 'number') continue;
    const start = getAppointmentDateTime(apt, apt.startTime);
    if (start <= now) continue;
    const forCase = map[apt.caseId] ?? {};
    const existing = forCase[apt.sessionNumber];
    if (!existing || start < getAppointmentDateTime(existing, existing.startTime)) {
      forCase[apt.sessionNumber] = apt;
    }
    map[apt.caseId] = forCase;
  }
  return map;
}

/** Chip label: „Astăzi 10:00”, „Mâine 10:00”, or „Joi 21 aug · 10:00”. */
export function formatNextAppointmentChipLabel(
  appointment: Appointment,
  now = new Date()
): string {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(
    appointment.date.getFullYear(),
    appointment.date.getMonth(),
    appointment.date.getDate()
  );
  const time = appointment.startTime;

  if (target.getTime() === today.getTime()) return `${t.dashboard.today} ${time}`;
  if (target.getTime() === tomorrow.getTime()) return `Mâine ${time}`;

  const weekday = appointment.date.toLocaleDateString('ro-RO', { weekday: 'short' });
  const dayMonth = appointment.date.toLocaleDateString('ro-RO', {
    day: 'numeric',
    month: 'short',
  });
  const weekdayLabel = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  return `${weekdayLabel} ${dayMonth} - ${time}`;
}

export function getCalendarDatePath(date: Date): string {
  return `/calendar?date=${toDateKey(date)}`;
}

/** Overdue by frequency, but only when no future appointment is already scheduled. */
export function isFrequencyOverdueActionable(
  caseItem: Pick<Case, 'id' | 'lastMeetingDate' | 'meetingFrequencyWeeks'>,
  nextAppointmentByCaseId: Record<string, Appointment>,
  now = new Date()
): boolean {
  if (!caseItem.meetingFrequencyWeeks || !caseItem.lastMeetingDate) return false;
  if (nextAppointmentByCaseId[caseItem.id]) return false;
  return isMeetingFrequencyOverdue(
    caseItem.lastMeetingDate,
    caseItem.meetingFrequencyWeeks,
    now
  );
}

export function getActiveCases(cases: Case[], limit?: number): Case[] {
  const active = cases
    .filter((c) => c.status === 'active')
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  return limit !== undefined ? active.slice(0, limit) : active;
}

/** Appointments within this window surface as „Pregătește sesiunea”. */
export const PREPARE_SESSION_WINDOW_MS = 48 * 60 * 60 * 1000;

/**
 * Whether to still ask for a session's tablou.
 *
 * The ask has a deliberately short life: it opens when the report closes a session and goes quiet
 * again the moment the next meeting is close enough to prepare for — asking a counselor to look
 * back at the last session while they are getting ready for the next one is asking at the wrong
 * moment. It is computed rather than stored, so a meeting that moves or is cancelled reopens the
 * window on its own, and a closed case stops asking without anything having to clear the flag.
 *
 * Going quiet is not losing it: the tablou stays reachable from its note for good.
 */
export function shouldAskForTablou(
  caseItem: Pick<Case, 'status' | 'pendingClarifySession'>,
  isPreparingNextSession: boolean
): boolean {
  return (
    caseItem.status === 'active' &&
    caseItem.pendingClarifySession != null &&
    !isPreparingNextSession
  );
}

export interface DashboardActionMetrics {
  reportsNeeded: number;
  casesWithoutConsent: number;
  frequencyOverdue: number;
  prepareSession: number;
  activeCases: number;
}

export interface DashboardActionCaseBuckets {
  reportsNeededCaseIds: string[];
  casesWithoutConsentIds: string[];
  frequencyOverdueCaseIds: string[];
  prepareSessionCaseIds: string[];
}

export type TodayAttentionKind =
  | 'prepare'
  | 'report'
  | 'frequency'
  | 'consent'
  | 'monthly_report'
  | 'clarify';

export interface TodayAttentionItem {
  kind: TodayAttentionKind;
  /** Case id, or a stable sentinel for non-case items (e.g. monthly report). */
  caseId: string;
  caseItem?: Case;
  appointment?: Appointment;
  /** Road session index when known from the appointment. */
  sessionNumber?: number | null;
  title: string;
  meta: string;
  monthKey?: string;
  monthlyReportPhase?: 'due' | 'overdue';
}

export interface TodayFocusModel {
  primary: TodayAttentionItem | null;
  secondary: TodayAttentionItem[];
}

export interface TodayFocusMonthlyReportInput {
  missing: boolean;
  phase: 'due' | 'overdue';
  monthKey: string;
  monthLabel: string;
}

export function computeDashboardActionMetrics(
  cases: Case[],
  appointments: Appointment[],
  sessionReportCounts: Record<string, number>,
  now = new Date()
): DashboardActionMetrics {
  const buckets = collectDashboardActionCaseBuckets(
    cases,
    appointments,
    sessionReportCounts,
    now
  );
  return {
    reportsNeeded: buckets.reportsNeededCaseIds.length,
    casesWithoutConsent: buckets.casesWithoutConsentIds.length,
    frequencyOverdue: buckets.frequencyOverdueCaseIds.length,
    prepareSession: buckets.prepareSessionCaseIds.length,
    activeCases: cases.filter((caseItem) => caseItem.status === 'active').length,
  };
}

/** Soonest future appointment per active case within the prepare window. */
export function collectPrepareSessionAppointments(
  cases: Case[],
  appointments: Appointment[],
  now = new Date(),
  windowMs = PREPARE_SESSION_WINDOW_MS
): Map<string, Appointment> {
  const activeIds = new Set(
    cases.filter((c) => c.status === 'active').map((c) => c.id)
  );
  const byCase = new Map<string, Appointment>();
  const windowEnd = now.getTime() + windowMs;

  appointments.forEach((appointment) => {
    if (!appointment.caseId || !activeIds.has(appointment.caseId)) return;
    const start = getAppointmentDateTime(appointment, appointment.startTime);
    const startMs = start.getTime();
    if (startMs <= now.getTime() || startMs > windowEnd) return;
    const existing = byCase.get(appointment.caseId);
    if (
      !existing ||
      startMs < getAppointmentDateTime(existing, existing.startTime).getTime()
    ) {
      byCase.set(appointment.caseId, appointment);
    }
  });

  return byCase;
}

export function collectDashboardActionCaseBuckets(
  cases: Case[],
  appointments: Appointment[],
  sessionReportCounts: Record<string, number>,
  now = new Date()
): DashboardActionCaseBuckets {
  const latestPastAppointmentByCaseId = new Map<string, Appointment>();
  appointments.forEach((appointment) => {
    if (!appointment.caseId) return;
    const endAt = getAppointmentDateTime(
      appointment,
      appointment.endTime || appointment.startTime
    );
    if (endAt.getTime() > now.getTime()) return;
    const current = latestPastAppointmentByCaseId.get(appointment.caseId);
    if (
      !current ||
      getAppointmentDateTime(current, current.endTime || current.startTime).getTime() <
        endAt.getTime()
    ) {
      latestPastAppointmentByCaseId.set(appointment.caseId, appointment);
    }
  });

  const nextAppointmentByCaseId = buildNextAppointmentByCaseId(appointments, now);
  const prepareByCaseId = collectPrepareSessionAppointments(cases, appointments, now);

  const reportsNeededCaseIds: string[] = [];
  const casesWithoutConsentIds: string[] = [];
  const frequencyOverdueCaseIds: string[] = [];
  const prepareSessionCaseIds: string[] = [];

  cases.forEach((caseItem) => {
    if (caseItem.status !== 'active') return;

    const latestPastAppointment = latestPastAppointmentByCaseId.get(caseItem.id);
    const lastMeetingDate = caseItem.lastMeetingDate;
    // Reports carry a meeting date (no time), so the appointment counts as
    // covered whenever the last reported meeting is on its day or later.
    const needsReportAfterAppointment = Boolean(
      latestPastAppointment &&
        (!lastMeetingDate ||
          startOfDay(lastMeetingDate).getTime() <
            startOfDay(
              getAppointmentDateTime(
                latestPastAppointment,
                latestPastAppointment.endTime || latestPastAppointment.startTime
              )
            ).getTime())
    );
    const isOverdueByFrequency = isFrequencyOverdueActionable(
      caseItem,
      nextAppointmentByCaseId,
      now
    );
    if (needsReportAfterAppointment) reportsNeededCaseIds.push(caseItem.id);
    if (!caseItem.consentAttached) casesWithoutConsentIds.push(caseItem.id);
    if (isOverdueByFrequency) frequencyOverdueCaseIds.push(caseItem.id);
    if (prepareByCaseId.has(caseItem.id)) prepareSessionCaseIds.push(caseItem.id);
  });

  return {
    reportsNeededCaseIds,
    casesWithoutConsentIds,
    frequencyOverdueCaseIds,
    prepareSessionCaseIds,
  };
}

function sessionLabelForAppointment(
  appointment: Appointment | undefined,
  reportCount: number
): number {
  if (typeof appointment?.sessionNumber === 'number') {
    return Math.max(0, Math.floor(appointment.sessionNumber));
  }
  return Math.max(0, Math.floor(reportCount));
}

/**
 * Focus card model: soonest prepare wins as primary; otherwise first report,
 * then frequency, then consent. Remaining items become secondary rows (max 4).
 */
export const TODAY_SECONDARY_LIMIT = 4;

export function buildTodayFocusModel(
  cases: Case[],
  appointments: Appointment[],
  sessionReportCounts: Record<string, number>,
  now = new Date(),
  monthlyReport?: TodayFocusMonthlyReportInput | null
): TodayFocusModel {
  const buckets = collectDashboardActionCaseBuckets(
    cases,
    appointments,
    sessionReportCounts,
    now
  );
  const caseById = new Map(cases.map((c) => [c.id, c]));
  const prepareByCaseId = collectPrepareSessionAppointments(cases, appointments, now);

  const items: TodayAttentionItem[] = [];

  const prepareSorted = [...prepareByCaseId.entries()].sort(
    (a, b) =>
      getAppointmentDateTime(a[1], a[1].startTime).getTime() -
      getAppointmentDateTime(b[1], b[1].startTime).getTime()
  );
  prepareSorted.forEach(([caseId, appointment]) => {
    const caseItem = caseById.get(caseId);
    if (!caseItem) return;
    const sessionNumber = sessionLabelForAppointment(
      appointment,
      sessionReportCounts[caseId] ?? 0
    );
    items.push({
      kind: 'prepare',
      caseId,
      caseItem,
      appointment,
      sessionNumber,
      title: t.dashboard.todayFocus.prepareTitle.replace('{n}', String(sessionNumber)),
      meta: `${caseItem.counseledName} · ${formatNextAppointmentChipLabel(appointment, now)}`,
    });
  });

  const latestPastByCaseId = new Map<string, Appointment>();
  appointments.forEach((appointment) => {
    if (!appointment.caseId) return;
    const endAt = getAppointmentDateTime(
      appointment,
      appointment.endTime || appointment.startTime
    );
    if (endAt.getTime() > now.getTime()) return;
    const current = latestPastByCaseId.get(appointment.caseId);
    if (
      !current ||
      getAppointmentDateTime(current, current.endTime || current.startTime).getTime() <
        endAt.getTime()
    ) {
      latestPastByCaseId.set(appointment.caseId, appointment);
    }
  });

  buckets.reportsNeededCaseIds.forEach((caseId) => {
    const caseItem = caseById.get(caseId);
    if (!caseItem) return;
    const reportCount = sessionReportCounts[caseId] ?? 0;
    const pastAppointment = latestPastByCaseId.get(caseId);
    /** Session that still needs a report (not the last already written). */
    const sessionNumber = sessionLabelForAppointment(pastAppointment, reportCount);
    items.push({
      kind: 'report',
      caseId,
      caseItem,
      appointment: pastAppointment,
      sessionNumber,
      title: t.dashboard.todayFocus.reportTitle,
      meta: `${t.sessionReports.sessionNumber} #${sessionNumber} · ${caseItem.counseledName}`,
    });
  });

  buckets.frequencyOverdueCaseIds.forEach((caseId) => {
    const caseItem = caseById.get(caseId);
    if (!caseItem) return;
    items.push({
      kind: 'frequency',
      caseId,
      caseItem,
      title: t.dashboard.todayFocus.frequencyTitle,
      meta: caseItem.counseledName,
    });
  });

  buckets.casesWithoutConsentIds.forEach((caseId) => {
    const caseItem = caseById.get(caseId);
    if (!caseItem) return;
    items.push({
      kind: 'consent',
      caseId,
      caseItem,
      title: t.dashboard.todayFocus.consentTitle,
      meta: caseItem.counseledName,
    });
  });

  /**
   * Deferred, never urgent: it never enters `priority` below, so it can only ever land in
   * `secondary` — a low-priority nudge, not something that can bump a real alert out of the
   * primary slot. Computed straight from the case's own field, already part of `cases`, so this
   * costs nothing beyond what the dashboard already loads.
   */
  cases.forEach((caseItem) => {
    if (!shouldAskForTablou(caseItem, prepareByCaseId.has(caseItem.id))) return;
    items.push({
      kind: 'clarify',
      caseId: caseItem.id,
      caseItem,
      sessionNumber: caseItem.pendingClarifySession,
      title: t.cases.cockpit.clarifyPending.replace(
        '{n}',
        String(caseItem.pendingClarifySession)
      ),
      meta: caseItem.counseledName,
    });
  });

  if (monthlyReport?.missing) {
    const overdue = monthlyReport.phase === 'overdue';
    items.push({
      kind: 'monthly_report',
      caseId: `monthly_report:${monthlyReport.monthKey}`,
      title: overdue
        ? t.dashboard.todayFocus.monthlyReportOverdueTitle
        : t.dashboard.todayFocus.monthlyReportDueTitle,
      meta: monthlyReport.monthLabel,
      monthKey: monthlyReport.monthKey,
      monthlyReportPhase: monthlyReport.phase,
    });
  }

  if (items.length === 0) {
    return { primary: null, secondary: [] };
  }

  /** Overdue monthly can be primary; due-only stays in secondary. */
  const priority: TodayAttentionKind[] =
    monthlyReport?.missing && monthlyReport.phase === 'overdue'
      ? ['prepare', 'report', 'monthly_report', 'frequency', 'consent']
      : ['prepare', 'report', 'frequency', 'consent'];

  let primary: TodayAttentionItem | null = null;
  for (const kind of priority) {
    const match = items.find((item) => item.kind === kind);
    if (match) {
      primary = match;
      break;
    }
  }

  /** If only due monthly exists, keep primary empty (all-clear card) and list it secondary. */
  if (!primary) {
    return {
      primary: null,
      secondary: items.slice(0, TODAY_SECONDARY_LIMIT),
    };
  }

  const secondary = items
    .filter((item) => !(item.kind === primary.kind && item.caseId === primary.caseId))
    .slice(0, TODAY_SECONDARY_LIMIT);

  return { primary, secondary };
}
